(function () {
    'use strict';

    angular.module('app').factory('OrganisationRemarkValidator', OrganisationRemarkValidatorFactory);

    OrganisationRemarkValidatorFactory.$inject = [];
    function OrganisationRemarkValidatorFactory() {

        /**
         * Contains invalid SectionCodes for remarks based on the scope of the remark.
         */
        const invalidRemarkCodesBySection = {
            identity: ['111'],
            activity: ['151'],
            file: ['161'],
            persons_phase1: ['141'],
            persons: ['171'],
            relations: ['175'],
            justification: ['121'],
            justifications: ['123'],
            signatures: ['161'],
        };

        /**
         * Contains valid SectionCodes for remarks based on the scope of the remark.
         */
        const validRemarkCodesBySection = {
            identity: ['112'],
            activity: ['152'],
            file: ['162'],
            persons_phase1: ['142'],
            persons: ['170'],
            relations: ['176'],
            justification: ['122'],
            justifications: ['124'],
            signatures: ['162'],
        };

        /**
         * The security office can add additional comments in the identity tab and the justifications tab.
         * These comments are identified by the section codes in this array.
         */
        const securityOfficerCommentSections = ['110', '120'];

        const validationTypes = {
            IsValid: true,
            IsInvalid: false
        };

        return {
            IsSectionChecked: isSectionChecked,
            IsValidatedPhase1: (request) => isValidated(request, validRemarkCodesBySection.persons_phase1),
            IsValidated: (request) => isValidated(request, validRemarkCodesBySection.persons),
            IsInvalidatedPhase1: (request) => isInvalidated(request, invalidRemarkCodesBySection.persons_phase1),
            IsInvalidated: (request) => isInvalidated(request, invalidRemarkCodesBySection.persons),
            IsSectionInvalidated: isSectionInvalidated,
            IsItemChecked: isItemChecked,
        };

        //#region Public

        function isValidated(request, section) {
            if (!request)
                return false;

            const validSections = validRemarkCodesBySection.identity.concat(validRemarkCodesBySection.activity)
                .concat(validRemarkCodesBySection.justifications)
                .concat(validRemarkCodesBySection.relations)
                .concat(validRemarkCodesBySection.signatures);

            const validationRemarks = request.Remarks.filter(r => !securityOfficerCommentSections.includes(r.Section));

            const isRequestValidated = checkRemarks(validationRemarks, validSections, validationTypes.IsValid);

            return isRequestValidated && checkJustificationRemarks(request, validRemarkCodesBySection.justification, validationTypes.IsValid) && checkPersonRemarks(request, section, validationTypes.IsValid);
        }

        function isInvalidated(request, section) {
            if (!request)
                return false;

            const invalidSections = invalidRemarkCodesBySection.identity.concat(invalidRemarkCodesBySection.activity).concat(invalidRemarkCodesBySection.justifications).concat(invalidRemarkCodesBySection.relations).concat(invalidRemarkCodesBySection.signatures);
            const isRequestInvalidated = checkRemarks(request.Remarks, invalidSections, validationTypes.IsInvalid);

            return isRequestInvalidated || checkJustificationRemarks(request, invalidRemarkCodesBySection.justification, validationTypes.IsInvalid) || checkPersonRemarks(request, section, validationTypes.IsInvalid);
        }

        /**
         * Checks a section for NOK remarks with a reason
         * @param {Object} request 
         * @param {String} section 
         * @returns {Boolean}
         */
        function isSectionInvalidated(request, section) {
            if (!request || !section)
                return false;

            const normalizedSection = section.toLowerCase();
            switch (normalizedSection) {
                case 'identity':
                case 'activity':
                case 'signatures':
                    return containsRemarkForSection(request.Remarks, invalidRemarkCodesBySection.FVM[normalizedSection]) || containsIndeterminateRemark(request.Remarks);
                case 'justification':
                    const allJustifications = getAllJustifications(request);

                    return checkRemarksOnArray(allJustifications, containsRemarkForSection, invalidRemarkCodesBySection[normalizedSection])
                        || checkRemarksOnArray(allJustifications, containsIndeterminateRemark, invalidRemarkCodesBySection[normalizedSection]);
                case 'relations':
                    return checkRemarksOnArray(request.Persons, containsRemarkForSection, invalidRemarkCodesBySection[normalizedSection])
                        || checkRemarksOnArray(request.Persons, containsIndeterminateRemark, invalidRemarkCodesBySection[normalizedSection]);
                default:
                    return false;
            }
        }

        function isSectionChecked(request, section, fallback = false) {
            if (!request || !section)
                return false;

            const normalizedSection = section.toLowerCase();
            switch (normalizedSection) {
                case 'identity':
                case 'activity':
                case 'signatures':
                    return containsRemarkForSection(request.Remarks, validRemarkCodesBySection[normalizedSection], fallback);
                case 'justification':
                    const allJustifications = getAllJustifications(request);

                    return checkRemarksOnArray(
                        allJustifications,
                        containsRemarkForSection,
                        validRemarkCodesBySection[normalizedSection]
                    ) && containsRemarkForSection(request.Remarks, validRemarkCodesBySection.justifications, fallback);
                case 'persons_phase1': // Needed for 'normalizedSection' parameter
                case 'persons':
                    return checkRemarksOnArray(
                        request.Persons,
                        containsRemarkForSection,
                        validRemarkCodesBySection[normalizedSection]
                    ) && containsRemarkForSection(request.Remarks, validRemarkCodesBySection.relations, fallback);
                default:
                    return true;
            }
        }

        function isItemChecked(item, section, fallback = false) {
            if (!item || !section)
                return false;

            const normalizedSection = section.toLowerCase();
            const sectionsToCheck = validRemarkCodesBySection[normalizedSection].concat(invalidRemarkCodesBySection[normalizedSection]);
            return containsRemarkForSection(item.Remarks, sectionsToCheck, fallback);
        }

        //#endregion

        /**
         * 
         * @param {Array} subItems 
         * @param {Function} validationFunction 
         * @param {Array} codes 
         * @returns {Boolean}
         */
        function checkRemarksOnArray(subItems, validationFunction, codes) {
            if (!subItems || subItems.length === 0)
                return false;

            for (const item of subItems) {
                if (!validationFunction(item.Remarks, codes))
                    return false;
            }
            return true;
        }

        /**
         * Checks if an array of remarks contains an indeterminate remark
         * @param {Array} remarks 
         * @returns {Boolean} true if the array contains an indeterminate remark
         */
        function containsIndeterminateRemark(remarks) {
            if (!remarks)
                return false;

            return remarks.some(r => r.indeterminate === true);
        }

        /**
         * Checks if any remarks with a section code corresponding to invalid data exists
         * @param {Array} remarks The remarks to check
         * @param {Array} sections The section codes for NOK remarks
         * @param {Boolean} fallback The default value to use when no remarks are present
         * @returns {Boolean} true if any of the remarks has an invalid code
         */
        function containsRemarkForSection(remarks, sections, fallback) {
            if (!sections)
                return false;
            if (!remarks || remarks.length === 0)
                return fallback;

            return remarks.some(r => sections.includes(r.Section));
        }

        /**
         * Checks if all remarks have a Section property contained in the codes parameter and every remark has a Description
         * @param {Array} remarks The remarks
         * @param {Array} sections The sections
         * @param {Boolean} validationType IsValid or IsInvalid
         * @returns {Boolean}
         */
        function checkRemarks(remarks, sections, validationType) {
            if (validationType === validationTypes.IsInvalid)
                return remarks.length > 0 && remarks.some(r => sections.includes(r.Section)) && remarks.filter(r => sections.includes(r.Section)).every(r => sections.includes(r.Section) && r.Description && r.Description.length > 0);
            else
                return remarks.length > 0 && remarks.map(r => r.Section).every(s => sections.includes(s));
        }

        function checkJustificationRemarks(request, sections, validationType) {
            const allJustifications = getAllJustifications(request);

            return checkRemarks(allJustifications.flatMap(j => j.Remarks ? j.Remarks : []), sections, validationType);
        }

        function checkPersonRemarks(request, sections, validationType) {
            if (!request.Persons || request.Persons.length === 0)
                return validationType;

            if (validationType === validationTypes.IsValid) {
                return request.Persons.flatMap(p => p.Remarks ? p.Remarks : []).filter(r => sections.includes(r.Section)).length === request.Persons.length && checkRemarks(request.Persons.flatMap(p => p.Remarks ? p.Remarks.filter(r => sections.includes(r.Section)) : []), sections, validationType);
            }

            return checkRemarks(request.Persons.flatMap(p => p.Remarks ? p.Remarks : []), sections, validationType);
        }

        function getAllJustifications(request) {
            const allJustifications = [];

            if (request.Justifications)
                allJustifications.push(...request.Justifications);

            if (request.CopiedJustifications) {
                allJustifications.push(...request.CopiedJustifications.filter(j => j.Action !== null));
            }
            return allJustifications;
        }
    }
})();
